import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { queryFeatureSummary } from '@/features/features/data';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildFeatureCounts } from '@/features/quality/components/qualityStatusDataHelpers';
import { useQualityDateRange, type QualityRange } from '@/features/quality/hooks';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import { buildDeliveryPageFilters } from '@/features/shared/utils/deliveryPageFilters';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import {
  queryKeys,
  useIsAuthenticated,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import { buildInFilter } from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureStatus } from '@flowqa/shared';

type UseFeaturesPageViewModelResult = {
  range: QualityRange;
  setRange: (value: QualityRange) => void;
  selectedStatuses: FeatureStatus[];
  selectedTags: string[];
  selectedProjects: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  projectFilterOptions: Array<{ value: string; label: string }>;
  showProjectFilter: boolean;
  onStatusToggle: (status: FeatureStatus) => void;
  onStatusesChange: (nextStatuses: FeatureStatus[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onProjectsChange: (nextProjects: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  featureMetricCards: ReturnType<typeof buildStatusMetricCards>;
  featureMetricCardsLoading: boolean;
  dateFiltersEnabled: boolean;
};

export const useFeaturesPageViewModel = (): UseFeaturesPageViewModelResult => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const [range, setRangeState] = useState<QualityRange>('7d');
  const [selectedStatuses, setSelectedStatuses] = useState<FeatureStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:features');
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { rangeStartIso, rangeEndIso } = useQualityDateRange(range);
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const dateFiltersEnabled = true;
  const showDelta = false;

  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const projectFilters = useMemo(
    () => (showProjectFilter ? buildInFilter('projectId', selectedProjects) : []),
    [selectedProjects, showProjectFilter],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const { summaryFilters } = useMemo(
    () =>
      buildDeliveryPageFilters({
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso: null,
        previousRangeEndIso: null,
        statusFilters,
        tagFilters,
        projectFilters,
      }),
    [projectFilters, rangeEndIso, rangeStartIso, statusFilters, tagFilters],
  );
  const featureSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const featureSummaryFiltersKey = useMemo(
    () => JSON.stringify(featureSummaryInput.filters),
    [featureSummaryInput.filters],
  );
  const featureSummaryQuery = useQuery({
    queryKey: queryKeys.featureSummary(
      effectiveProjectId,
      selectedOrganisationId ?? null,
      featureSummaryFiltersKey,
    ),
    enabled: isAuthenticated && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryFeatureSummary(
        effectiveProjectId,
        selectedOrganisationId ?? undefined,
        featureSummaryInput,
      ),
  });
  const featureSummary = featureSummaryQuery.data ?? null;
  const featureCounts = useMemo(() => buildFeatureCounts(featureSummary), [featureSummary]);
  const featureMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(featureStatusOptions, resolveMetricTone),
    [],
  );
  const featureMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: featureMetricOptions,
        counts: featureCounts,
        deltas: undefined,
        total: featureSummary?.total,
        totalLabel: 'Features',
        previousCounts: {},
        previousTotal: undefined,
        loading: featureSummaryQuery.isFetching && !featureSummaryQuery.isError,
        showDelta,
      }),
    [
      featureCounts,
      featureMetricOptions,
      featureSummary?.total,
      featureSummaryQuery.isError,
      featureSummaryQuery.isFetching,
      showDelta,
    ],
  );
  const tableFilters = summaryFilters;
  const onStatusToggle = useCallback((status: FeatureStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const onStatusesChange = useCallback((nextStatuses: FeatureStatus[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  const setRange = useCallback((value: QualityRange) => {
    setRangeState(value);
  }, []);

  return {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange: setSelectedTags,
    onProjectsChange: handleProjectsChange,
    tableFilters,
    featureMetricCards,
    featureMetricCardsLoading: featureSummaryQuery.isFetching && !featureSummaryQuery.isError,
    dateFiltersEnabled,
  };
};
